import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { assignTechnician } from '../api/assignTechnicianApiService';
import { ASSIGN_TECHNICIAN_LABELS as L } from '../constants/labels';
import { ASSIGN_TECHNICIAN_ERROR_MESSAGES } from '../errors/errorMessages';
import type { TaskFormListItemCertificate } from '@/epics/taskFormManagement/types';
import type {
  AssigneeType,
  AssignTechnicianResponseData,
} from '../types/assignTechnicianType';

/**
 * Which kind of assignee the assign modal is targeting. The empty string is the
 * initial "nothing chosen yet" state — the toggle stays unselected (and disabled
 * until a window is picked), mirroring the reschedule modal.
 */
export type AssignToRole = AssigneeType | '';

const MIN_DURATION_MINUTES = 30;

const TIME_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

const DEFAULT_START_TIME = '00:00';
const DEFAULT_END_TIME = '23:59';

const toIsoDate = (d: Date | undefined): string =>
  d ? format(d, 'yyyy-MM-dd') : '';

/**
 * Combine a Date (date-only) with an HH:mm wall-clock string into a single Date.
 * Returns null if either input is missing or time is malformed.
 */
const combineDateTime = (date: Date | undefined, time: string): Date | null => {
  if (!date) {
    return null;
  }
  if (!TIME_REGEX.test(time)) {
    return null;
  }
  const [hh, mm] = time.split(':').map(Number);
  const combined = new Date(date);
  combined.setHours(hh ?? 0, mm ?? 0, 0, 0);
  return combined;
};

interface UseAssignTechnicianOptions {
  serviceRequestId: string | undefined;
  onSuccess?: (data?: AssignTechnicianResponseData) => void;
}

export interface UseAssignTechnicianReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;

  // Window
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
  setStartDate: (d: Date | undefined) => void;
  setEndDate: (d: Date | undefined) => void;
  setStartTime: (t: string) => void;
  setEndTime: (t: string) => void;
  startDateError: string;
  endDateError: string;
  endTimeError: string;
  windowComplete: boolean;

  // Task form
  selectedTaskFormId: string;
  selectedTaskFormFormId: string;
  selectedTaskFormName: string;
  setSelectedTaskForm: (
    id: string,
    formId: string,
    name: string,
    certificate: TaskFormListItemCertificate | null
  ) => void;
  taskFormError: string;

  // Certificate auto-populate (populated synchronously from the task form data)
  linkedCertificateName: string;
  linkedCertificateId: string;
  noCertificateError: string;

  // Assignee type toggle (RR-912)
  assignToRole: AssignToRole;
  setAssignToRole: (role: AssignToRole) => void;
  assignToError: string;

  // Technician
  selectedTechnicianId: string;
  selectedTechnicianCode: string;
  setSelectedTechnician: (id: string, technicianCode: string) => void;
  technicianError: string;

  // Admin
  selectedAdminId: string;
  selectedAdminCode: string;
  setSelectedAdmin: (id: string, adminCode: string) => void;
  adminError: string;

  // Submission
  isSubmitting: boolean;
  submitError: string;
  canSubmit: boolean;
  submit: () => Promise<void>;

  // Derived ISO strings for the eligibility query.
  startDateIso: string;
  endDateIso: string;
}

export const useAssignTechnician = ({
  serviceRequestId,
  onSuccess,
}: UseAssignTechnicianOptions): UseAssignTechnicianReturn => {
  const [isOpen, setIsOpen] = useState(false);

  const [startDate, setStartDateState] = useState<Date | undefined>(undefined);
  const [endDate, setEndDateState] = useState<Date | undefined>(undefined);
  const [startTime, setStartTimeState] = useState<string>('');
  const [endTime, setEndTimeState] = useState<string>('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  // Task form state
  const [selectedTaskFormId, setSelectedTaskFormIdState] = useState<string>('');
  const [selectedTaskFormFormId, setSelectedTaskFormFormIdState] =
    useState<string>('');
  const [selectedTaskFormName, setSelectedTaskFormNameState] =
    useState<string>('');
  const [taskFormError, setTaskFormError] = useState('');

  // Certificate state — populated synchronously from the task form's embedded data
  const [linkedCertificateName, setLinkedCertificateName] =
    useState<string>('');
  const [linkedCertificateId, setLinkedCertificateId] = useState<string>('');
  const [noCertificateError, setNoCertificateError] = useState('');

  // Starts unselected (mirrors the reschedule modal): the toggle is disabled
  // until a window is chosen, and a role must be picked before submit.
  const [assignToRole, setAssignToRoleState] = useState<AssignToRole>('');
  const [assignToError, setAssignToError] = useState('');

  const [selectedTechnicianId, setSelectedTechnicianIdState] =
    useState<string>('');
  const [selectedTechnicianCode, setSelectedTechnicianCodeState] =
    useState<string>('');
  const [technicianError, setTechnicianError] = useState('');

  const [selectedAdminId, setSelectedAdminIdState] = useState<string>('');
  const [selectedAdminCode, setSelectedAdminCodeState] = useState<string>('');
  const [adminError, setAdminError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const resetForm = useCallback(() => {
    setStartDateState(undefined);
    setEndDateState(undefined);
    setStartTimeState('');
    setEndTimeState('');
    setStartDateError('');
    setEndDateError('');
    setEndTimeError('');
    setSelectedTaskFormIdState('');
    setSelectedTaskFormFormIdState('');
    setSelectedTaskFormNameState('');
    setTaskFormError('');
    setLinkedCertificateName('');
    setLinkedCertificateId('');
    setNoCertificateError('');
    setAssignToRoleState('');
    setAssignToError('');
    setSelectedTechnicianIdState('');
    setSelectedTechnicianCodeState('');
    setTechnicianError('');
    setSelectedAdminIdState('');
    setSelectedAdminCodeState('');
    setAdminError('');
    setSubmitError('');
  }, []);

  const open = useCallback(() => {
    resetForm();
    setIsOpen(true);
  }, [resetForm]);

  const close = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    resetForm();
  }, [isSubmitting, resetForm]);

  const windowComplete = Boolean(startDate && endDate);

  const validateDateOrder = useCallback((): string => {
    if (!startDate || !endDate) {
      return '';
    }
    const startDay = new Date(startDate);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(endDate);
    endDay.setHours(0, 0, 0, 0);
    if (endDay.getTime() < startDay.getTime()) {
      return L.ERROR_START_DATE_AFTER_END;
    }
    return '';
  }, [startDate, endDate]);

  const validateTimeOrder = useCallback((): string => {
    if (!startTime || !endTime) {
      return '';
    }
    if (!TIME_REGEX.test(startTime) || !TIME_REGEX.test(endTime)) {
      return '';
    }
    // When both dates are present, compare combined datetimes so a multi-day
    // window with identical clock-times (e.g. Day 1 10:00 → Day 2 10:00) is
    // still a valid forward-going range. Without dates, fall back to a pure
    // string compare so the user gets immediate feedback as they type.
    if (startDate && endDate) {
      const start = combineDateTime(startDate, startTime);
      const end = combineDateTime(endDate, endTime);
      if (start && end && end.getTime() <= start.getTime()) {
        return L.ERROR_START_TIME_AFTER_END;
      }
      return '';
    }
    if (endTime <= startTime) {
      return L.ERROR_START_TIME_AFTER_END;
    }
    return '';
  }, [startDate, endDate, startTime, endTime]);

  const validateMinDuration = useCallback((): string => {
    if (!startDate || !endDate) {
      return '';
    }
    const start = combineDateTime(startDate, startTime || DEFAULT_START_TIME);
    const end = combineDateTime(endDate, endTime || DEFAULT_END_TIME);
    if (!start || !end) {
      return '';
    }
    const durationMinutes = (end.getTime() - start.getTime()) / 60000;
    if (durationMinutes > 0 && durationMinutes < MIN_DURATION_MINUTES) {
      return L.ERROR_MIN_DURATION;
    }
    return '';
  }, [startDate, endDate, startTime, endTime]);

  // Live-validate per-field whenever any window field changes. Required
  // errors (set on submit) only persist for fields that are still empty —
  // once both dates are filled we recompute the validation errors fresh.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (startDate && endDate) {
      setEndDateError(validateDateOrder());
    }
    const timeErr = validateTimeOrder() || validateMinDuration();
    setEndTimeError(timeErr);
  }, [
    isOpen,
    startDate,
    endDate,
    startTime,
    endTime,
    validateDateOrder,
    validateTimeOrder,
    validateMinDuration,
  ]);

  const setStartDate = useCallback((d: Date | undefined) => {
    setStartDateState(d);
    setStartDateError('');
    setEndDateError('');
    setAssignToError('');
    setSelectedTechnicianIdState('');
    setSelectedTechnicianCodeState('');
    setTechnicianError('');
    setSelectedAdminIdState('');
    setSelectedAdminCodeState('');
    setAdminError('');
    setSubmitError('');
  }, []);

  const setEndDate = useCallback((d: Date | undefined) => {
    setEndDateState(d);
    setEndDateError('');
    setAssignToError('');
    setSelectedTechnicianIdState('');
    setSelectedTechnicianCodeState('');
    setTechnicianError('');
    setSelectedAdminIdState('');
    setSelectedAdminCodeState('');
    setAdminError('');
    setSubmitError('');
  }, []);

  const setStartTime = useCallback((t: string) => {
    setStartTimeState(t);
    setEndTimeError('');
    setAssignToError('');
    setSelectedTechnicianIdState('');
    setSelectedTechnicianCodeState('');
    setTechnicianError('');
    setSelectedAdminIdState('');
    setSelectedAdminCodeState('');
    setAdminError('');
    setSubmitError('');
  }, []);

  const setEndTime = useCallback((t: string) => {
    setEndTimeState(t);
    setEndTimeError('');
    setAssignToError('');
    setSelectedTechnicianIdState('');
    setSelectedTechnicianCodeState('');
    setTechnicianError('');
    setSelectedAdminIdState('');
    setSelectedAdminCodeState('');
    setAdminError('');
    setSubmitError('');
  }, []);

  // Certificate data comes directly from the task form list item — no extra API call needed.
  const setSelectedTaskForm = useCallback(
    (
      id: string,
      formId: string,
      name: string,
      certificate: TaskFormListItemCertificate | null
    ) => {
      setSelectedTaskFormIdState(id);
      setSelectedTaskFormFormIdState(formId);
      setSelectedTaskFormNameState(name);
      setTaskFormError('');
      setLinkedCertificateName('');
      setLinkedCertificateId('');
      setNoCertificateError('');

      if (!id) {
        return;
      }

      if (certificate) {
        setLinkedCertificateName(certificate.name);
        setLinkedCertificateId(certificate.cert_template_id);
      } else {
        setNoCertificateError(L.ERROR_NO_CERTIFICATE_LINKED);
      }
    },
    []
  );

  const setSelectedTechnician = useCallback(
    (id: string, technicianCode: string) => {
      setSelectedTechnicianIdState(id);
      setSelectedTechnicianCodeState(technicianCode);
      setTechnicianError('');
    },
    []
  );

  const setSelectedAdmin = useCallback((id: string, adminCode: string) => {
    setSelectedAdminIdState(id);
    setSelectedAdminCodeState(adminCode);
    setAdminError('');
  }, []);

  // Switching the assignee type clears the other side's picker + errors so a
  // stale technician/admin selection can't leak into the submit payload.
  const setAssignToRole = useCallback((role: AssignToRole) => {
    setAssignToRoleState(role);
    setAssignToError('');
    setSubmitError('');
    if (role === 'admin') {
      setSelectedTechnicianIdState('');
      setSelectedTechnicianCodeState('');
      setTechnicianError('');
    } else {
      setSelectedAdminIdState('');
      setSelectedAdminCodeState('');
      setAdminError('');
    }
  }, []);

  const canSubmit = !isSubmitting && !!serviceRequestId;

  const submit = useCallback(async () => {
    if (!serviceRequestId) {
      return;
    }

    let hasError = false;
    if (!startDate) {
      setStartDateError(L.ERROR_REQUIRED);
      hasError = true;
    }
    if (!endDate) {
      setEndDateError(L.ERROR_REQUIRED);
      hasError = true;
    }
    if (startDate && endDate) {
      const dateErr = validateDateOrder();
      if (dateErr) {
        setEndDateError(dateErr);
        hasError = true;
      }
    }
    const timeErr = validateTimeOrder() || validateMinDuration();
    if (timeErr) {
      setEndTimeError(timeErr);
      hasError = true;
    }
    if (!selectedTaskFormId) {
      setTaskFormError(L.ERROR_REQUIRED);
      hasError = true;
    } else if (noCertificateError || !linkedCertificateId) {
      // Task form selected but no linked certificate — error already visible in the UI
      hasError = true;
    }
    // Require a role once the window is set (matches the reschedule modal —
    // the toggle is disabled until then, so requiring it earlier is moot).
    if (startDate && endDate && !assignToRole) {
      setAssignToError(L.ERROR_REQUIRED);
      hasError = true;
    }
    if (assignToRole === 'admin') {
      if (!selectedAdminId) {
        setAdminError(L.ERROR_REQUIRED);
        hasError = true;
      }
    } else if (assignToRole === 'technician' && !selectedTechnicianId) {
      setTechnicianError(L.ERROR_REQUIRED);
      hasError = true;
    }
    if (hasError) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const result = await assignTechnician({
        serviceRequestId,
        assigneeType: assignToRole === 'admin' ? 'admin' : 'technician',
        technicianId:
          assignToRole === 'admin' ? selectedAdminId : selectedTechnicianId,
        startDate: toIsoDate(startDate),
        endDate: toIsoDate(endDate),
        startTime,
        endTime,
        ...(selectedTaskFormFormId ? { formId: selectedTaskFormFormId } : {}),
      });
      if (result.success) {
        setIsOpen(false);
        resetForm();
        onSuccess?.(result.data);
      } else if (result.conflict) {
        // Assignee already booked for the window — highlight the relevant
        // assignee dropdown inline instead of showing a general submit error.
        if (assignToRole === 'admin') {
          setAdminError(
            result.error ?? ASSIGN_TECHNICIAN_ERROR_MESSAGES.ADMIN_CONFLICT
          );
        } else {
          setTechnicianError(
            result.error ?? ASSIGN_TECHNICIAN_ERROR_MESSAGES.TECHNICIAN_CONFLICT
          );
        }
      } else {
        setSubmitError(
          result.error ?? ASSIGN_TECHNICIAN_ERROR_MESSAGES.DEFAULT_ERROR
        );
      }
    } catch {
      setSubmitError(ASSIGN_TECHNICIAN_ERROR_MESSAGES.UNEXPECTED_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    serviceRequestId,
    selectedTaskFormId,
    selectedTaskFormFormId,
    linkedCertificateId,
    noCertificateError,
    assignToRole,
    selectedTechnicianId,
    selectedAdminId,
    startDate,
    endDate,
    startTime,
    endTime,
    validateDateOrder,
    validateTimeOrder,
    validateMinDuration,
    onSuccess,
    resetForm,
  ]);

  return {
    isOpen,
    open,
    close,
    startDate,
    endDate,
    startTime,
    endTime,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
    startDateError,
    endDateError,
    endTimeError,
    windowComplete,
    selectedTaskFormId,
    selectedTaskFormFormId,
    selectedTaskFormName,
    setSelectedTaskForm,
    taskFormError,
    linkedCertificateName,
    linkedCertificateId,
    noCertificateError,
    assignToRole,
    setAssignToRole,
    assignToError,
    selectedTechnicianId,
    selectedTechnicianCode,
    setSelectedTechnician,
    technicianError,
    selectedAdminId,
    selectedAdminCode,
    setSelectedAdmin,
    adminError,
    isSubmitting,
    submitError,
    canSubmit,
    submit,
    startDateIso: toIsoDate(startDate),
    endDateIso: toIsoDate(endDate),
  };
};
